/**
 * Canonical role-based permission predicates — INSTALLER / specialist model.
 *
 * Single source of truth for "what can this installer-side user do?". Replaces
 * the ad-hoc `access === 'ADMIN' || role === 'admin' || role === 'owner'`
 * checks that were scattered across the app with subtly different definitions
 * (some admitted `owner`, some didn't; some honored the unified `role`, some
 * only the legacy `access` field; casing was assumed).
 *
 * DECISION (2026-06-14): installer-side authorization is derived from the
 * UNIFIED ROLE only (`user_companies.role`). The legacy `access` field
 * (ADMIN / STANDARD / DIRECT) is NO LONGER consulted. Any installer account
 * must have a `role` set or it falls to the lowest privilege.
 *
 * Installer role hierarchy:  owner > admin > manager > technician ≈ viewer
 *
 * These are pure predicates over a `{ role }`-shaped object (the `user` prop /
 * `userAPI` record). They intentionally do NOT cover the dealer role model
 * (admin/manager/finance/reporting/contact) — that's a separate pass.
 */

export const INSTALLER_ROLE_RANK = {
  owner: 4,
  admin: 3,
  manager: 2,
  technician: 1,
  viewer: 1,
};

/**
 * Normalize a user-like value to a lowercase role string.
 * Accepts the user object ({ role }) or a bare role string.
 */
export function getRole(userOrRole) {
  if (!userOrRole) return '';
  const role = typeof userOrRole === 'string' ? userOrRole : userOrRole.role;
  return (role || '').toLowerCase();
}

function rank(userOrRole) {
  return INSTALLER_ROLE_RANK[getRole(userOrRole)] || 0;
}

/** owner or admin — full company/settings/integration/user management. */
export function isInstallerAdmin(userOrRole) {
  return rank(userOrRole) >= INSTALLER_ROLE_RANK.admin;
}

/** owner only — top of the hierarchy. */
export function isOwner(userOrRole) {
  return getRole(userOrRole) === 'owner';
}

/** owner/admin/manager — can take actions (settle/void), manage staff & jobs. */
export function isManagerOrAbove(userOrRole) {
  return rank(userOrRole) >= INSTALLER_ROLE_RANK.manager;
}

export function isTechnician(userOrRole) {
  return getRole(userOrRole) === 'technician';
}

export function isViewer(userOrRole) {
  return getRole(userOrRole) === 'viewer';
}

/** view-only installer roles — excluded from management/edit surfaces. */
export function isViewerOrTechnician(userOrRole) {
  const r = getRole(userOrRole);
  return r === 'viewer' || r === 'technician';
}

// ---------------------------------------------------------------------------
// Intent-named capability aliases — prefer these at call sites for readability.
// Each maps to exactly one rank predicate so the policy lives in one place.
// ---------------------------------------------------------------------------

/** Company settings, user management, integrations, purchasing. */
export const canManageCompany = isInstallerAdmin;

/** Staff management (installer: admin/owner/manager). */
export const canManageStaff = isManagerOrAbove;

/** Edit the service catalog / pricing. */
export const canManageServices = isInstallerAdmin;

/** Inventory: everyone except view-only viewers. */
export function canManageInventory(userOrRole) {
  return !isViewer(userOrRole);
}

// ===========================================================================
// DEALER model — a SEPARATE role vocabulary: admin / manager / finance /
// reporting / contact. Capability-shaped, NOT a linear rank (two "full" roles,
// one financial, one read-only, one no-access). Pure role predicates; callers
// gate `userType === 'dealer'` themselves.
//
// NB: "referrals enabled" is intentionally NOT modeled here. Whether a dealer
// sees Referrals is a separate per-user capability flag (`access === 'DIRECT'`
// → no referrals), independent of role — do not fold it into these predicates.
// ===========================================================================

/** No access — locked out of the dealer interface. */
export function isDealerContact(userOrRole) {
  return getRole(userOrRole) === 'contact';
}

/** Dealer admin — account/settings-level actions (e.g. Find an Installer). */
export function isDealerAdmin(userOrRole) {
  return getRole(userOrRole) === 'admin';
}

/** "Any function" roles — full operational access (admin or manager). */
export function canDealerManage(userOrRole) {
  const r = getRole(userOrRole);
  return r === 'admin' || r === 'manager';
}

/** Staff management — admin, manager, finance (not reporting/contact). */
export function canDealerAccessStaff(userOrRole) {
  const r = getRole(userOrRole);
  return r === 'admin' || r === 'manager' || r === 'finance';
}

export function isDealerFinance(userOrRole) {
  return getRole(userOrRole) === 'finance';
}

export function isDealerReporting(userOrRole) {
  return getRole(userOrRole) === 'reporting';
}

// ===========================================================================
// Cross-model capability gates (span both vocabularies — pass userType).
// ===========================================================================

/**
 * Job Messaging (dealer↔specialist conversation thread).
 *
 * GATED TO ADMINS for the initial rollout — testing with a select cohort before
 * full release. Installer side: owner/admin (isInstallerAdmin). Dealer side:
 * admin (isDealerAdmin). Callers pass the active `userType` ('dealer' |
 * 'installer'). To open the feature to everyone, relax this predicate.
 */
export function canUseJobMessaging(userOrRole, userType) {
  return userType === 'dealer'
    ? isDealerAdmin(userOrRole)
    : isInstallerAdmin(userOrRole);
}
